import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import {
  FormControl,
  FormLabel,
  GridItem,
  HStack,
  InputGroup,
  InputRightAddon,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Spacer,
  useShowMessage,
} from '../../../core-ui';
import { useShowApolloError } from '../../../support/graphql/useShowApolloError';

import {
  GET_PCT_BY_CATEGORY_FEES,
  UPDATE_PCT_BY_CATEGORY_FEE,
} from './graphql';

import { GetPctByCategoryFees } from './__generated__/GetPctByCategoryFees';
import {
  UpdatePctByCategoryFee,
  UpdatePctByCategoryFeeVariables,
} from './__generated__/UpdatePctByCategoryFee';
import { useSeason } from '../../SeasonState';

export function PercentageByCategory() {
  const showMessage = useShowMessage();
  const showApolloError = useShowApolloError();
  const { season } = useSeason();
  const seasonId = season?.id ?? '';

  const { data } = useQuery<GetPctByCategoryFees>(GET_PCT_BY_CATEGORY_FEES, {
    variables: { seasonId },
    fetchPolicy: 'cache-and-network',
  });

  const [updatePctByCategoryFee] = useMutation<
    UpdatePctByCategoryFee,
    UpdatePctByCategoryFeeVariables
  >(UPDATE_PCT_BY_CATEGORY_FEE, {
    onError: showApolloError,
    onCompleted: () => {
      showMessage(
        'success',
        t('Percentage by category fee updated successfully'),
      );
    },
    refetchQueries: [GET_PCT_BY_CATEGORY_FEES],
  });

  const pctByCategoryFees = data?.pctByCategoryFees ?? [];

  return (
    <SimpleGrid columns={2} gap={2} p={10} maxHeight="60vh" overflow="auto">
      {pctByCategoryFees.map((fee) => {
        const { id, categoryId, categoryName, percentage } = fee;
        return (
          <CategoryInput
            key={categoryId}
            category={categoryName}
            onBlur={async (event) => {
              const newPercentage = Number(event.target.value);
              if (newPercentage === percentage) {
                return;
              }
              await updatePctByCategoryFee({
                variables: { id, updates: { percentage: newPercentage } },
              });
            }}
            value={percentage}
          />
        );
      })}
    </SimpleGrid>
  );
}

type CategoryInputProps = {
  category: string;
  onBlur: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value: number;
};

function CategoryInput(props: CategoryInputProps) {
  const { category, onBlur, value } = props;

  const [inputValue, setInputValue] = useState(value);

  return (
    <GridItem colSpan={2}>
      <FormControl>
        <HStack width="60%" mx="auto">
          <FormLabel>{category}</FormLabel>
          <Spacer />
          <InputGroup width="120px">
            <NumberInput defaultValue={inputValue}>
              <NumberInputField
                textAlign="right"
                onBlur={onBlur}
                onChange={(event) => setInputValue(event.target.valueAsNumber)}
              />
            </NumberInput>
            <InputRightAddon>%</InputRightAddon>
          </InputGroup>
        </HStack>
      </FormControl>
    </GridItem>
  );
}
